import { Link, useLocation } from "react-router-dom";
import { Phone, MessageSquare, Menu, X, ShieldCheck, CalendarClock } from "lucide-react";
import { useState } from "react";
import { PHONE, PHONE_TEL } from "@/lib/constants";
import { InspectionModal } from "@/components/site/InspectionModal";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const nav = [
    { to: "/", label: "Home" },
    { to: "/shingles", label: "Shingles" },
  ];

  return (
    <header data-testid="site-header" className="sticky top-0 z-40">
      {/* Top alert ticker strip */}
      <div
        data-testid="header-ticker"
        className="relative overflow-hidden border-b border-white/15"
        style={{
          background:
            "linear-gradient(90deg, #020617 0%, #0c1a3d 25%, #1e3a8a 50%, #0c1a3d 75%, #020617 100%)",
        }}
      >
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-32 pointer-events-none opacity-40"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(125,211,252,0.55) 50%, transparent 100%)",
            animation: "sdpScan 6s linear infinite",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-2 flex items-center justify-between gap-4 text-[11px] sm:text-xs font-impact uppercase tracking-[0.22em] text-white">
          <div className="flex items-center gap-2">
            <CalendarClock size={14} className="text-sky-300" />
            <span>Schedule Online Anytime · 24/7</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="ticker-phone"
              className="flex items-center gap-1.5 hover:text-sky-300 transition-colors"
            >
              <Phone size={12} />
              <span>{PHONE}</span>
            </a>
            <span className="text-white/30 hidden sm:inline">|</span>
            <a
              href={`sms:${PHONE_TEL}`}
              data-testid="ticker-text"
              className="flex items-center gap-1.5 hover:text-sky-300 transition-colors"
            >
              <MessageSquare size={12} />
              <span>Text</span>
            </a>
          </div>
        </div>
        <style>{`@keyframes sdpScan{0%{transform:translateX(-10%)}100%{transform:translateX(2400%)}}`}</style>
      </div>

      {/* Slim nav bar */}
      <div
        className="relative border-b-2 border-red-600"
        style={{
          background:
            "linear-gradient(180deg, #020617 0%, #0b1220 100%)",
        }}
      >
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between py-3 gap-4">
          {/* Compact brand chip */}
          <Link
            to="/"
            data-testid="brand-logo"
            aria-label="Storm Damage Pros — Home"
            className="flex items-center gap-3 group shrink-0"
          >
            <div className="leading-none">
              <div
                className="font-impact uppercase text-xl sm:text-2xl tracking-tight"
                style={{
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #cbd5e1 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                Storm Damage Pros
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mt-0.5">
                JD Dean · Storm Specialist
              </div>
            </div>
          </Link>

          {/* Right side: nav + CTA */}
          <div className="flex items-center gap-6">
            <nav className="hidden lg:flex items-center gap-7">
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative text-sm uppercase tracking-widest font-bold transition-colors ${
                    pathname === n.to
                      ? "text-white"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  {n.label}
                  {pathname === n.to && (
                    <span className="absolute -bottom-2 left-0 right-0 h-[3px] bg-red-600" />
                  )}
                </Link>
              ))}
              <InspectionModal>
                <button
                  type="button"
                  data-testid="nav-free-inspection"
                  className="text-sm uppercase tracking-widest font-bold text-slate-300 hover:text-white transition-colors"
                >
                  Free Inspection
                </button>
              </InspectionModal>
            </nav>

            <InspectionModal>
              <button
                type="button"
                data-testid="header-cta"
                className="hidden md:inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-impact uppercase tracking-wider text-sm px-5 py-3 border-2 border-white shadow-[0_0_0_2px_#dc2626,0_6px_22px_rgba(220,38,38,0.55)] hover:shadow-[0_0_0_2px_#fff,0_8px_28px_rgba(125,211,252,0.55)] transition-all hover:-translate-y-0.5"
                style={{ clipPath: "polygon(8% 0%, 100% 0%, 92% 100%, 0% 100%)" }}
              >
                <ShieldCheck size={16} className="text-white" />
                Free Inspection
              </button>
            </InspectionModal>

            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white hover:text-sky-300 transition-colors"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Bottom barber stripe */}
        <div
          aria-hidden
          className="absolute -bottom-[2px] left-0 right-0 h-1"
          style={{
            background:
              "repeating-linear-gradient(90deg, #dc2626 0 28px, #ffffff 28px 32px, #020617 32px 60px, #1e3a8a 60px 64px)",
          }}
        />
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t-2 border-red-600 bg-slate-950">
          <div className="px-6 py-6 flex flex-col gap-5">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base uppercase tracking-widest font-bold text-white hover:text-sky-300 transition-colors"
              >
                {n.label}
              </Link>
            ))}
            <InspectionModal>
              <button
                type="button"
                data-testid="mnav-free-inspection"
                onClick={() => setOpen(false)}
                className="text-left text-base uppercase tracking-widest font-bold text-red-500"
              >
                Free Inspection
              </button>
            </InspectionModal>
            <div className="flex gap-3 pt-2">
              <a
                href={`sms:${PHONE_TEL}`}
                className="flex-1 bg-transparent border-2 border-white text-white font-bold uppercase px-4 py-3 inline-flex items-center justify-center gap-2 text-sm"
              >
                <MessageSquare size={16} /> Text
              </a>
              <a
                href={`tel:${PHONE_TEL}`}
                className="flex-1 bg-red-600 text-white font-bold uppercase px-4 py-3 inline-flex items-center justify-center gap-2 text-sm border-2 border-white"
              >
                <Phone size={16} /> Call
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
